<template>
  <div class="xform-section">
    <div class="xfields">
      <base-form-field
        :schema="addressSchema.address1"
        v-model="address.address1"
        @update:model-value="handleFieldChange('address1', $event)"
      />
      <div class="xfield form-field xfield-group" v-if="false">
        <div class="input-group" :disabled="disabled">
          <div class="input-wrapper-outer">
            <label class="label cuscd-form-field-label" for="addressUnitTypeId">Unit Type</label>
            <VeeField
              name="addressUnitTypeId"
              v-model="address.addressUnitTypeId"
              v-slot="{ field }"
              @handleChange="handleFieldChange('addressUnitTypeId', $event)"
            >
              <select
                class="form-control select"
                v-model="address.addressUnitTypeId"
                v-bind="field"
              >
                <option :value="null"></option>
                <option
                  v-for="option in addressUnitTypes"
                  :key="option.id"
                  :title="option.name"
                  :selected="option.id === Number(address.addressUnitTypeId)"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
            </VeeField>
            <div>
              <ErrorMessage class="error-message" name="addressUnitTypeId" />
            </div>
          </div>
          <div class="input-wrapper-outer" style="flex: 1">
            <label class="label cuscd-form-field-label" for="address2">Unit Number</label>
            <div class="input-wrapper-inner">
              <span class="prefix">{{ selectedUnitType?.abbreviation }}</span>
              <VeeField
                type="text"
                name="address2"
                v-model="address.address2"
                @handleChange="handleFieldChange('address2', $event)"
              />
            </div>
            <div>
              <ErrorMessage class="error-message" name="address2" />
            </div>
          </div>
        </div>
      </div>
      <div class="xfield form-field">
        <div class="input-wrapper-outer">
          <label class="label cuscd-form-field-label" for="addressUnitTypeId">Unit Type</label>
          <VeeField
            name="addressUnitTypeId"
            v-model="address.addressUnitTypeId"
            v-slot="{ field }"
            @handleChange="handleFieldChange('addressUnitTypeId', $event)"
          >
            <select class="form-control select" v-model="address.addressUnitTypeId" v-bind="field">
              <option :value="null"></option>
              <option
                v-for="option in addressUnitTypes"
                :key="option.id"
                :title="option.name"
                :selected="option.id === Number(address.addressUnitTypeId)"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
          </VeeField>
          <div>
            <ErrorMessage class="error-message" name="addressUnitTypeId" />
          </div>
        </div>
      </div>
      <div class="xfield form-field xfield-group">
        <div class="input-wrapper-outer" style="flex: 1">
          <label class="label cuscd-form-field-label" for="address2">Unit Number</label>
          <div class="input-wrapper-inner">
            <span class="prefix">{{ selectedUnitType?.abbreviation }}</span>
            <VeeField
              type="text"
              name="address2"
              v-model="address.address2"
              @handleChange="handleFieldChange('address2', $event)"
            />
          </div>
          <div>
            <ErrorMessage class="error-message" name="address2" />
          </div>
        </div>
      </div>
      <base-form-field
        :schema="addressSchema.city"
        v-model="address.city"
        @update:model-value="handleFieldChange('city', $event)"
      />
      <base-form-field
        :schema="addressSchema.stateId"
        v-model.number="address.stateId"
        :options="statesOptions"
        @update:model-value="handleFieldChange('stateId', $event)"
      />
      <base-form-field
        :schema="addressSchema.postalCode"
        v-model="address.postalCode"
        @update:model-value="handleFieldChange('postalCode', $event)"
      />
      <base-form-field
        v-if="countryOptions?.length > 1"
        :schema="addressSchema.countryId"
        v-model.number="address.countryId"
        :options="countryOptions"
        @update:model-value="handleFieldChange('countryId', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, toRefs } from "vue";
import { AddressCountryIds, AddressTypeIds } from "@/config/constants";
import type { Address } from "@/models/application";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { Field as VeeField, ErrorMessage } from "vee-validate";
import { FormModes } from "@/models/form";
import useStudentAddressForm from "@/composables/forms/useStudentAddressForm";
import { useStore } from "vuex";
import type { StudentVM } from "@/models/student";

const store = useStore();

const props = withDefaults(
  defineProps<{
    mode: keyof typeof FormModes;
    disabled: boolean;
  }>(),
  {
    disabled: false,
  },
);

const { mode } = toRefs(props);

const address = ref<Address>({
  address1: "",
  address2: null as null | string,
  addressTypeId: AddressTypeIds.PermanentAddress,
  addressTypeName: null,
  addressUnitTypeId: null as null | number,
  addressUnitTypeName: null as null | string,
  applicantId: 0,
  id: 0,
  city: "",
  stateId: 0,
  stateName: "",
  postalCode: "",
  countryId: AddressCountryIds.USA,
  countryName: "",
});

const selectedStudent = computed<StudentVM | undefined>(
  () => store.state.loanConfigurationModule.applicationInfo.student,
);

const originalAddress = computed<Address>(() => {
  if (selectedStudent.value?.addresses && selectedStudent.value?.addresses?.length > 0) {
    return selectedStudent.value?.addresses[0];
  }
  return defaultAddress.value;
});

const updatedAddress = computed<Address>(() => {
  return {
    address1: values?.address1 || "",
    address2: formattedAddress2.value,
    addressTypeId: address.value?.addressTypeId || AddressTypeIds.PermanentAddress,
    addressTypeName: address.value?.addressTypeName || "",
    addressUnitTypeId: address.value?.addressUnitTypeId || null,
    addressUnitTypeName: address.value?.addressUnitTypeName || null,
    applicantId: address.value?.applicantId || selectedStudent.value?.applicantId || 0,
    id: address.value?.id || 0,
    city: address.value?.city || "",
    stateId: address.value?.stateId || 0,
    stateName: address.value?.stateName || "",
    postalCode: address.value?.postalCode || "",
    countryId: address.value?.countryId || 0,
    countryName: address.value?.countryName || "",
  };
});

const {
  addressBackup,
  addressSchema,
  addressUnitTypes,
  countryOptions,
  defaultAddress,
  discardChanges,
  errors,
  formatAddress2,
  formattedAddress2,
  formatZipCode,
  handleFieldChange,
  hasErrors,
	prefixes,
  resetSchemas,
  selectedUnitType,
  setValues,
  setFieldValue,
  setAddress,
  statesOptions,
  values,
} = useStudentAddressForm({
  address,
  mode,
  initialValues: originalAddress,
});


defineExpose({
  address,
  addressBackup,
  discardChanges,
  errors,
  formatAddress2,
  formattedAddress2,
  formatZipCode,
  hasErrors,
  originalAddress,
	prefixes,
  resetSchemas,
  setAddress,
  setFieldValue,
  setValues,
  updatedAddress,
  values,
});
</script>

<style scoped src="@/styles/baseformfield.scss" lang="scss"></style>
<style scoped src="@/styles/address2.css"></style>
<style scoped src="@/styles/student/student-information-form.css"></style>
